import {IconButton} from '@albert-io/atomic';
import classnames from 'classnames';
import React from 'react';

import AnnotatableContent from 'client/components/PracticeView/Annotations/AnnotatableContent/AnnotatableContent';
import {
  ANNOTATION_SHIELD_EXEMPT_PROPS,
  lockedControlProps
} from 'client/components/PracticeView/Annotations/shared/annotationFreeze';
import {buildQuestionOptionRegionId} from 'client/components/PracticeView/Annotations/shared/annotationRegions';
import {useOptionEliminatorStore} from 'client/components/PracticeView/OptionEliminatorStore/OptionEliminatorStore';
import {callTargetedAction} from 'client/framework';
import CorrectnessIndicatorWrapper from 'client/QuestionTypes/common/CorrectnessIndicatorWrapper/CorrectnessIndicatorWrapper';
import {MarkdownWithSupplement} from 'client/components/QuestionTypes/shared/MarkdownWithSupplement';
import {ALTERNATE_CONTENT_LANGUAGES} from 'client/components/QuestionTypes/shared/TranslationSection/Translation.types';

import multipleChoiceQuestionActions from './MultipleChoiceQuestion.actions';

interface Props {
  index: number;
  isCorrectAnswer: boolean;
  isGuessSubmitted: boolean;
  isSelected: boolean;
  isSelectMultiple: boolean;
  option: Record<string, any>;
  targetStore: string;
  questionId: string;
  // translation props
  /** Raw translation payload value — absent while it loads. The render falls back to the original text and the anchor lang stays null. */
  translatedOptionText?: string;
  showTranslation?: boolean;
  // elimination props (for legacy wrapper)
  onEliminate?: () => void;
  areAnswersLocked?: boolean;
}

export default function MultipleChoiceOption({
  index,
  isCorrectAnswer,
  isGuessSubmitted,
  isSelected,
  isSelectMultiple,
  option,
  targetStore,
  showTranslation = false,
  translatedOptionText,
  questionId,
  onEliminate,
  areAnswersLocked = false
}: Props) {
  const optionId = option.getId();
  const translatedText = showTranslation ? translatedOptionText : undefined;
  const {getQuestionState, updateQuestionState} = useOptionEliminatorStore();
  const isOptionEliminated = getQuestionState(questionId)?.includes(optionId) || false;

  const wrapperClass = classnames('mcq-option', {
    'mcq-option--selected': isSelected,
    'mcq-option--unselected': !isSelected,
    'mcq-option--pre-submission': !isGuessSubmitted,
    'mcq-option--post-submission': isGuessSubmitted,
    'mcq-option--eliminated': isOptionEliminated && !isGuessSubmitted,
    'mcq-option--checkbox': isSelectMultiple
  });

  function handleClick() {
    if (isGuessSubmitted || isOptionEliminated) {
      return;
    }

    callTargetedAction({
      name: multipleChoiceQuestionActions.SELECT_OPTION,
      payload: option.getId(),
      targetStore
    });
  }

  function handleEliminate() {
    // Use eliminator context logic
    if (!isOptionEliminated && onEliminate) {
      onEliminate();
    }
    updateQuestionState(questionId, (questionState) => {
      return questionState.includes(optionId)
        ? questionState.delete(optionId)
        : questionState.add(optionId);
    });
  }

  return (
    <div data-testid={`mcq-option-${index}`} className='mcq-option-accessible-wrapper'>
      <input
        {...lockedControlProps(areAnswersLocked)}
        className='mcq-option__hidden-input'
        type={isSelectMultiple ? 'checkbox' : 'radio'}
        onChange={handleClick}
        checked={isSelected}
        id={`input-${optionId}`}
      />
      <CorrectnessIndicatorWrapper
        as='label'
        htmlFor={`input-${optionId}`}
        className={wrapperClass}
        isGuessSubmitted={isGuessSubmitted}
        isCorrectAnswer={isCorrectAnswer}
        isSelected={isSelected}
      >
        <div
          className={classnames('mcq-option__letter-wrapper', {
            'mcq-option__letter-wrapper--checkbox': isSelectMultiple
          })}
        >
          <span className='mcq-option__letter'>
            {String.fromCharCode('A'.charCodeAt(0) + index)}
          </span>
        </div>
        <AnnotatableContent
          regionId={buildQuestionOptionRegionId(questionId, optionId)}
          lang={translatedText ? ALTERNATE_CONTENT_LANGUAGES[0] : null}
          className='contents'
        >
          <MarkdownWithSupplement
            text={translatedText || option.getValue()}
            className='mcq-option__content'
            isOptionEliminated={isOptionEliminated}
          />
        </AnnotatableContent>
        {!isGuessSubmitted && (
          <IconButton
            {...ANNOTATION_SHIELD_EXEMPT_PROPS}
            className='mcq-option__eliminate-btn !ml-auto !shrink-0'
            size='s'
            color='secondary'
            icon={isOptionEliminated ? 'eye-slash' : 'eye'}
            iconStyle='regular'
            label={isOptionEliminated ? 'Restore option' : 'Eliminate option'}
            onClick={handleEliminate}
          />
        )}
      </CorrectnessIndicatorWrapper>
    </div>
  );
}
